/**
 * Which tabs the Invite Client dialog shows, resolved once and shared: the strip needs it to
 * render and the dialog needs the count to size itself. Computed twice, they would disagree.
 */

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import {
  useActivePilotProgram,
  useIsPilotRestricted,
  usePilotAgentEnteredIntake,
  usePilotSelfPlanIntakeForm,
} from '@/shared/hooks/use-active-pilot-program';
import { useIsPerClientInviteLinkEnabled } from '@/shared/hooks/use-per-client-invite-link';
import { useInviteClientTrigger, type TabType } from '@/features/invite-client';

export interface InviteClientTabsState {
  allowedTabs: TabType[];
  /** The tab actually rendered, after clamping a stale deep link back to a permitted one. */
  resolvedTab: TabType;
  isEmailTabAvailable: boolean;
  /** Renames the email tab and bypasses the invite-client pilot restriction. */
  isPerClientInviteLink: boolean;
  hasSelfPlanIntakeForm: boolean;
  hasAgentEnteredIntake: boolean;
  showFillFormTab: boolean;
  /** True when only the share-a-link tab should render, with no tab strip at all. */
  isLinkOnly: boolean;
}

export function useInviteClientTabs(): InviteClientTabsState {
  const { flags } = useFeatureFlags();
  const { inviteClientTab } = useInviteClientTrigger();
  const isEdjPilot = useIsEdjPilotAdvisor();
  const pilotProgram = useActivePilotProgram();
  const hasSelfPlanIntakeForm = !!usePilotSelfPlanIntakeForm();
  // Re-opens "Fill Intake Form" behind the consent gate WITHOUT widening the program's
  // restrictions: the capability stays restricted, so nothing else about it changes.
  const hasAgentEnteredIntake = !!usePilotAgentEnteredIntake();
  const isInviteClientRestricted = useIsPilotRestricted('invite-client');
  const isIntakeFormRestricted = useIsPilotRestricted('intake-form');
  const isPerClientInviteLink = useIsPerClientInviteLinkEnabled();

  const isEmailTabAvailable = !isInviteClientRestricted || isPerClientInviteLink;
  const isLinkOnly =
    !!flags?.hideInviteEmailAndFillIntakeForm && !isEdjPilot && !isPerClientInviteLink;

  // Self-plan and fill-form used to be mutually exclusive. An agent-entered program needs BOTH:
  // the advisor can plan for themselves, and can also enter a client's information once that
  // client has given permission. `isIntakeFormRestricted` stays true either way — it is the
  // consent flow, not a relaxed restriction, that puts the tab back.
  const showFillFormTab = hasAgentEnteredIntake || !isIntakeFormRestricted;

  const allowedTabs: TabType[] = [
    'link',
    ...(isEmailTabAvailable ? (['email'] as TabType[]) : []),
    ...(hasSelfPlanIntakeForm ? (['self-test'] as TabType[]) : []),
    ...(showFillFormTab ? (['fill-form'] as TabType[]) : []),
  ];

  const currentTab = inviteClientTab || (isEmailTabAvailable ? 'email' : 'link');

  // A program's advisors can land on a tab their program withholds — via a stale deep
  // link, or by switching programs — so clamp back to the always-available link tab.
  const resolvedTab = pilotProgram && !allowedTabs.includes(currentTab) ? 'link' : currentTab;

  return {
    allowedTabs,
    resolvedTab,
    isEmailTabAvailable,
    isPerClientInviteLink,
    hasSelfPlanIntakeForm,
    hasAgentEnteredIntake,
    showFillFormTab,
    isLinkOnly,
  };
}
